import { useState } from 'react'
import {
  Box, HStack, VStack, Text, Badge, Link, Icon, Heading, Button, IconButton, Input, Field, Switch,
} from '@chakra-ui/react'
import { FiPhone, FiMail, FiClock, FiEdit2 } from 'react-icons/fi'
import { StatusChip } from '../shared'
import { useUpdateContact } from '../../hooks/useContactDetail'
import { extractApiMessage } from '../../lib/apiError'
import {
  normalizePhone, isValidPhone, normalizeEmail, isValidEmail, formatPhone, telHref,
  PHONE_ERROR, EMAIL_ERROR,
} from '../../lib/contactMethods'
import type { ContactDetail } from '../../types/contactDetail'

interface ContactMethodAreaProps {
  contact: ContactDetail
  /** UX only — the server gate is [Authorize(Roles = "Organizer,Admin")] on PUT /api/contacts/{id}. */
  canEdit: boolean
}

function isGoodTimeToContact(bestContactTime?: string): boolean {
  if (!bestContactTime) return false
  const hour = new Date().getHours()
  const time = bestContactTime.toLowerCase()
  if (time === 'morning' && hour >= 8 && hour < 12) return true
  if (time === 'afternoon' && hour >= 12 && hour < 17) return true
  if (time === 'evening' && hour >= 17 && hour < 21) return true
  return false
}

type MethodKind = 'phone' | 'email'

interface MethodRowProps {
  kind: MethodKind
  value?: string
  canEdit: boolean
  chip?: React.ReactNode
  extra?: React.ReactNode
  onSave: (value: string) => Promise<void>
}

function MethodRow({ kind, value, canEdit, chip, extra, onSave }: MethodRowProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const label = kind === 'phone' ? 'Phone' : 'Email'
  const icon = kind === 'phone' ? <FiPhone /> : <FiMail />

  const open = () => { setDraft(value ?? ''); setError(null); setEditing(true) }
  const cancel = () => { setEditing(false); setError(null) }

  const commit = async (raw: string) => {
    if (raw !== '') {
      const ok = kind === 'phone' ? isValidPhone(raw) : isValidEmail(raw)
      if (!ok) { setError(kind === 'phone' ? PHONE_ERROR : EMAIL_ERROR); return }
    }
    const payload = raw === ''
      ? ''
      : (kind === 'phone' ? normalizePhone(raw)! : normalizeEmail(raw)!)
    setBusy(true)
    try {
      await onSave(payload)
      setEditing(false)
      setError(null)
    } catch (err) {
      // A10: the server's 400 names the fix — never replace it with a generic failure line.
      setError(extractApiMessage(err, `Could not save ${label.toLowerCase()}`))
    } finally {
      setBusy(false)
    }
  }

  if (editing) {
    return (
      <Field.Root invalid={!!error}>
        <Field.Label>{label}</Field.Label>
        <VStack align="stretch" gap={2} w="full">
          <Input
            type={kind === 'phone' ? 'tel' : 'email'}
            inputMode={kind === 'phone' ? 'tel' : 'email'}
            autoComplete="off"
            autoFocus
            minH="44px"
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setError(null) }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void commit(draft.trim()) }
              if (e.key === 'Escape') { e.preventDefault(); cancel() }
            }}
          />
          {!!error && <Field.ErrorText>{error}</Field.ErrorText>}
          <HStack gap={2} flexWrap="wrap">
            <Button size="sm" minH="44px" colorPalette="brand" loading={busy}
              onClick={() => void commit(draft.trim())}>
              Save {label.toLowerCase()}
            </Button>
            <Button size="sm" minH="44px" variant="ghost" onClick={cancel}>Cancel</Button>
            {!!value && (
              <Button size="sm" minH="44px" variant="ghost" colorPalette="red"
                onClick={() => void commit('')}>
                Remove {label.toLowerCase()}
              </Button>
            )}
          </HStack>
        </VStack>
      </Field.Root>
    )
  }

  return (
    <Stackable>
      <HStack gap={3} flex="1" minW={0}>
        <Icon color="ink.2" asChild>{icon}</Icon>
        {value ? (
          <Link
            href={kind === 'phone' ? telHref(value) : `mailto:${value}`}
            color="accent" fontWeight="medium"
          >
            {kind === 'phone' ? formatPhone(value) : value}
          </Link>
        ) : (
          <Text color="ink.2">No {label.toLowerCase()} on file</Text>
        )}
        {chip}
        {extra}
      </HStack>
      {canEdit && (value
        ? (
          <IconButton aria-label={`Edit ${label.toLowerCase()}`} size="sm" variant="ghost"
            minH="44px" minW="44px" onClick={open}>
            <FiEdit2 />
          </IconButton>
        )
        : (
          <Button size="sm" variant="ghost" minH="44px" onClick={open}>
            Add {label.toLowerCase()}
          </Button>
        )
      )}
    </Stackable>
  )
}

/** 390px: the value and its action stack; from `sm` up they sit on one line. */
function Stackable({ children }: { children: React.ReactNode }) {
  return (
    <VStack
      align="stretch" gap={2} w="full"
      flexDirection={{ base: 'column', sm: 'row' }}
      justifyContent="space-between"
    >
      {children}
    </VStack>
  )
}

export function ContactMethodArea({ contact, canEdit }: ContactMethodAreaProps) {
  const bestTime = contact.voterRecord?.bestContactTime
  const goodTime = isGoodTimeToContact(bestTime)
  // Depend on `.mutateAsync`, never the whole mutation object — TanStack returns a NEW object
  // every render (memory: usemutation-unstable-object-deps).
  const { mutateAsync } = useUpdateContact(contact.id)
  const [dncError, setDncError] = useState<string | null>(null)

  const emailFromVoterFile = contact.emailSource === 'voterFile'

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <Heading size="sm" mb={4}>Contact Methods</Heading>

      <VStack gap={4} align="stretch">
        <MethodRow
          kind="phone"
          value={contact.phone}
          canEdit={canEdit}
          chip={contact.phone && contact.phoneIsAuthored
            ? <StatusChip variant="info">Manually entered</StatusChip> : undefined}
          extra={
            <>
              {contact.catalistData?.cellPhone && (
                <Badge bg="info.subtle" color="info.fg" size="sm">Cell</Badge>
              )}
              {contact.catalistData?.landlinePhone && (
                <Badge colorPalette="gray" size="sm">Landline</Badge>
              )}
            </>
          }
          onSave={(phone) => mutateAsync({ phone }).then(() => undefined)}
        />

        <MethodRow
          kind="email"
          value={contact.email}
          canEdit={canEdit}
          chip={
            emailFromVoterFile
              ? <StatusChip variant="neutral">From voter file</StatusChip>
              : (contact.email && contact.emailIsAuthored
                ? <StatusChip variant="info">Manually entered</StatusChip> : undefined)
          }
          onSave={(email) => mutateAsync({ email }).then(() => undefined)}
        />

        {bestTime && (
          <HStack gap={3}>
            <Icon color="ink.2" asChild><FiClock /></Icon>
            <Text fontSize="sm">Best time: {bestTime}</Text>
            {goodTime && <StatusChip variant="success">Good time to contact</StatusChip>}
          </HStack>
        )}

        {canEdit && (
          <VStack align="stretch" gap={1}>
            <HStack gap={3} justify="space-between" minH="44px">
              <Text fontSize="sm">Do not contact</Text>
              <Switch.Root
                checked={contact.isDoNotContact}
                colorPalette="brand"
                onCheckedChange={(e) => {
                  setDncError(null)
                  void mutateAsync({ isDoNotContact: e.checked === true })
                    .catch((err) => setDncError(extractApiMessage(err, 'Could not update do-not-contact')))
                }}
              >
                <Switch.HiddenInput role="switch" aria-label="Do not contact" />
                <Switch.Control><Switch.Thumb /></Switch.Control>
              </Switch.Root>
            </HStack>
            {!!dncError && <Text fontSize="sm" color="danger.fg">{dncError}</Text>}
          </VStack>
        )}
      </VStack>
    </Box>
  )
}
